import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster, toast } from 'sonner'
import { registerSW } from 'virtual:pwa-register'
import { initSentry, SentryErrorBoundary } from './lib/sentry'
import './index.css'
import App from './App.tsx'

// Initialize Sentry before React renders
initSentry()

// Register service worker with update handling
const updateSW = registerSW({
  onNeedRefresh() {
    toast('Ny versjon tilgjengelig', {
      description: 'Oppdaterer om 3 sekunder...',
      duration: 3000,
    })
    // Auto-reload after toast
    setTimeout(() => {
      updateSW(true)
    }, 3000)
  },
  onOfflineReady() {
    console.log('[PWA] App ready for offline use')
  },
})

// Click-to-dismiss toasts - dismiss all when any toast is clicked
document.addEventListener('click', (e) => {
  const target = e.target as HTMLElement
  if (target.closest('[data-sonner-toast]')) {
    toast.dismiss()
  }
})

// Console branding
const APP_VERSION = '1.8.7'
const ENV = import.meta.env.MODE
const BUILD_TIME = new Date().toISOString().split('T')[0]
const YEAR = new Date().getFullYear()

console.log(`
%c████████╗ ██████╗ ███╗   ███╗ █████╗ ████████╗██╗ ██████╗
╚══██╔══╝██╔═══██╗████╗ ████║██╔══██╗╚══██╔══╝██║██╔════╝
   ██║   ██║   ██║██╔████╔██║███████║   ██║   ██║██║
   ██║   ██║   ██║██║╚██╔╝██║██╔══██║   ██║   ██║██║
   ██║   ╚██████╔╝██║ ╚═╝ ██║██║  ██║   ██║   ██║╚██████╗
   ╚═╝    ╚═════╝ ╚═╝     ╚═╝╚═╝  ╚═╝   ╚═╝   ╚═╝ ╚═════╝
`, 'color: #10b981; font-family: monospace; font-weight: bold;')

console.log(
  `%cJohnsen Solutions%c  v${APP_VERSION}`,
  'color: #10b981; font-weight: bold; font-size: 14px;',
  'color: #6b7280; font-size: 12px;'
)
console.log(
  '%cDigital leveringsflyt for moderne lager',
  'color: #9ca3af; font-style: italic;'
)
console.log('')
console.log(
  `%cEnvironment:%c ${ENV}   %cBuild:%c ${BUILD_TIME}`,
  'color: #6b7280;', 'color: #10b981;',
  'color: #6b7280;', 'color: #10b981;'
)
console.log(
  `%cSupport:%c support@tomatic.no   %cWeb:%c tomatic.no`,
  'color: #6b7280;', 'color: #3b82f6;',
  'color: #6b7280;', 'color: #3b82f6; text-decoration: underline;'
)
console.log(
  `%c${YEAR} Johnsen Solutions. Alle rettigheter reservert.`,
  'color: #4b5563; font-size: 10px;'
)
console.log('')

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
})

function SentryFallback() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background p-4">
      <h1 className="text-xl font-bold text-destructive mb-2">Noe gikk galt</h1>
      <p className="text-muted-foreground mb-4">En uventet feil oppstod.</p>
      <button
        onClick={() => window.location.reload()}
        className="px-4 py-2 bg-primary text-primary-foreground rounded-md"
      >
        Last inn siden på nytt
      </button>
    </div>
  )
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <SentryErrorBoundary fallback={<SentryFallback />}>
      <QueryClientProvider client={queryClient}>
        <App />
        <Toaster
        position="top-center"
        toastOptions={{
          classNames: {
            toast: 'bg-card border-border text-foreground cursor-pointer active:scale-95 transition-transform',
            error: '!bg-destructive !text-white !border-destructive',
            title: 'text-inherit',
            description: 'text-inherit opacity-80',
          },
        }}
      />
      </QueryClientProvider>
    </SentryErrorBoundary>
  </StrictMode>
)
